// ./src/utils/api/VenueService.ts
//
// Venue (Library → the account's venue directory) service. OpResult-native.
// The account's own venues are returned with a `rights` array (a public-directory
// venue the caller doesn't own comes back read-only, rights: []). A bulk update
// (>1 id) may only change status and/or categories (server-enforced). See
// em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { VenueCategory } from "./VenueCategoryService";
import type { ServicePoint, ServicePointInput } from "./ServicePointService";
import type { VenueRequirement, VenueRequirementInput } from "./EventVendorService";

// =============================================================================
// Constants (mirror the eventsmagic-api VENUE_KIND boundary)
// =============================================================================

export const VENUE_KIND = {
  VENUE: "venue",
  VENDOR: "vendor",
} as const;
export type VenueKind = (typeof VENUE_KIND)[keyof typeof VENUE_KIND];

/** Resolved logo URL for a venue/vendor — the stored file's URL, else null. */
export const venueLogoUrl = (v?: { logoUploadedFile?: { fileUrl?: string | null } | null } | null): string | null =>
  v?.logoUploadedFile?.fileUrl || null;

// =============================================================================
// GraphQL
// =============================================================================

const VENUE_ADDRESS_FIELDS = `
  id
  label
  isPrimary
  phone
  contactName
  country
  stateProvince
  city
  postalCode
  streetAddress
  extraAddress
  latitude
  longitude
  isServiceBase
  serviceRadiusKm
  orderNo
`;

// Everything a list row needs — includes the resolved primaryAddress but NOT the
// full addresses array (that's only fetched for the editor via VENUE_FULL_FIELDS).
const VENUE_CORE_FIELDS = `
  id
  accountId
  kind
  visibility
  reviewStatus
  reviewReason
  name
  description
  website
  email
  phone
  contactName
  logoUploadedFileId
  logoUploadedFile { id fileUrl thumbnailUrl }
  capacity
  notes
  status
  rights
  createdAt
  updatedAt
  categories {
    id
    code
    name
    categoryGroup
    icon
  }
  primaryAddress { ${VENUE_ADDRESS_FIELDS} }
`;

// Core fields plus the full addresses array — used by get/getMany and the
// create/update mutations that feed the editor.
const VENUE_FULL_FIELDS = `
  ${VENUE_CORE_FIELDS}
  addresses { ${VENUE_ADDRESS_FIELDS} }
  servicePoints { id name kind activity notes phone guestNote address latitude longitude photoUploadedFileId photoUploadedFile { fileUrl thumbnailUrl } venueAddressId orderNo }
  vendorRequirements { id venueId venueCategoryId documentType documentKind name instructions dueDaysBefore orderNo }
`;

const VENUE_LIST_QUERY = gql`
  query VenueList($filter: VenueFilter, $params: PaginationAndSorting) {
    venueList(filter: $filter, params: $params) {
      code
      data { ${VENUE_CORE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VENUE_GET_QUERY = gql`
  query VenueGet($id: ID) {
    venueGet(id: $id) {
      code
      data { ${VENUE_FULL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VENUE_GET_MANY_QUERY = gql`
  query VenueGetMany($ids: [ID]) {
    venueGetMany(ids: $ids) {
      code
      data { ${VENUE_FULL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VENUE_CREATE_MUTATION = gql`
  mutation VenueCreate($params: VenueInput) {
    venueCreate(params: $params) {
      code
      data { ${VENUE_FULL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VENUE_UPDATE_MUTATION = gql`
  mutation VenueUpdate($where: VenueWhereInput, $params: VenueInput) {
    venueUpdate(where: $where, params: $params) {
      code
      data { ${VENUE_FULL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const VENUE_REMOVE_MUTATION = gql`
  mutation VenueRemove($where: VenueWhereInput) {
    venueRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Venue schema)
// =============================================================================

export interface VenueAddress {
  id?: string;
  label?: string | null;
  isPrimary?: boolean | null;
  phone?: string | null;
  contactName?: string | null;
  country?: string | null;
  stateProvince?: string | null;
  city?: string | null;
  postalCode?: string | null;
  streetAddress?: string | null;
  extraAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  isServiceBase?: boolean | null;
  serviceRadiusKm?: number | null;
  orderNo?: number | null;
}

// Nested address input under VenueInput.addresses. An `id` present = update that
// address; absent = create a new one; existing addresses omitted are removed.
export interface VenueAddressInput {
  id?: string | null;
  label?: string | null;
  isPrimary?: boolean | null;
  phone?: string | null;
  contactName?: string | null;
  country?: string | null;
  stateProvince?: string | null;
  city?: string | null;
  postalCode?: string | null;
  streetAddress?: string | null;
  extraAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  isServiceBase?: boolean | null;
  serviceRadiusKm?: number | null;
  orderNo?: number | null;
}

export interface Venue {
  id?: string;
  accountId?: string | null;
  /** 'venue' (hosts events) or 'vendor' (service provider). */
  kind?: string | null;
  visibility?: string | null;
  reviewStatus?: string | null;
  reviewReason?: string | null;
  name?: string | null;
  description?: string | null;
  website?: string | null;
  email?: string | null;
  phone?: string | null;
  contactName?: string | null;
  logoUploadedFileId?: string | null;
  /** The logo file resolved from ms_storage (fileUrl / thumbnailUrl). */
  logoUploadedFile?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  capacity?: number | null;
  notes?: string | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). Empty = read-only. */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
  categories?: VenueCategory[] | null;
  addresses?: VenueAddress[] | null;
  /** The primary address (or first) — populated for list rows. */
  primaryAddress?: VenueAddress | null;
  /** #169 — entrances, desks, merch tables (the owner's only; editor reads). */
  servicePoints?: ServicePoint[] | null;
  /** #170 — what this venue requires of the vendors working there (the owner's only). */
  vendorRequirements?: VenueRequirement[] | null;
}

export interface VenueInput {
  /** 'venue' (default) or 'vendor'. Set on create; immutable afterwards. */
  kind?: string | null;
  name?: string | null;
  description?: string | null;
  website?: string | null;
  email?: string | null;
  phone?: string | null;
  contactName?: string | null;
  logoUploadedFileId?: string | null;
  capacity?: number | null;
  notes?: string | null;
  status?: number | null;
  categoryIds?: string[] | null;
  /** The primary address (single-location convenience — the server marks it primary). */
  primaryAddress?: VenueAddressInput | null;
  /** Additional (non-primary) addresses. */
  addresses?: VenueAddressInput[] | null;
  /** #169 — the WHOLE list of points, in order (omit to leave them untouched). Refusals: servicePoints[<i>].<field>. */
  servicePoints?: ServicePointInput[];
  /** #170 — the WHOLE list of what the venue requires of vendors (omit to leave it). Refusals: vendorRequirements[<i>].<field>. */
  vendorRequirements?: VenueRequirementInput[];
}

// A bulk update (>1 id) may only mass-change status and/or categories (server-enforced).
export interface VenueBulkInput {
  status?: number | null;
  categoryIds?: string[] | null;
}

export interface VenueFilter {
  id?: string[] | null;
  kind?: string[] | null;
  scope?: string | null;
  visibility?: string[] | null;
  reviewStatus?: string[] | null;
  country?: string[] | null;
  stateProvince?: string[] | null;
  city?: string[] | null;
  categoryId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface VenueWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class VenueService {
  static async list(
    filter?: VenueFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(VENUE_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(VENUE_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(VENUE_GET_MANY_QUERY, { ids });
  }

  static async create(params: VenueInput): Promise<OpResult> {
    return ApiService.executeMutation(VENUE_CREATE_MUTATION, { params });
  }

  static async update(where: VenueWhereInput, params: VenueInput): Promise<OpResult> {
    return ApiService.executeMutation(VENUE_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(VENUE_REMOVE_MUTATION, { where: { id } });
  }

  /**
   * Mass-change status and/or categories on every selected venue in one call.
   * The server restricts a multi-id update to status + categories; a categoryIds
   * array REPLACES each selected venue's categories with that set.
   */
  static async bulkUpdate(ids: string[], params: VenueBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(VENUE_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected venue in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(VENUE_REMOVE_MUTATION, { where: { id: ids } });
  }
}
